import { Injectable, inject } from '@angular/core';
import { BackendService } from './backend.service';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { BaseResponse } from 'src/shared/models/base-response.model';
import {
	CustomReport,
	CustomReportsResponse,
	CustomReportResponse,
	SaveCustomReportData,
} from 'src/shared/models/custom-report.model';
import { map } from 'rxjs/operators';
import {
	PreCannedReport,
	PreCannedReportsResonse,
} from 'src/shared/models/pre-canned-report.model';
import {
	ReportSchedule,
	ReportSchedulesResonse,
	ReportScheduleResonse,
} from 'src/shared/models/report-schedule.model';
import {
	ReportHistory,
	ReportHistoryResonse,
} from 'src/shared/models/report-history.model';
import { ReportHtmlResonse } from 'src/shared/models/report-html.model';
import { CustomReportTablesResponse } from 'src/shared/models/custom-report-table.model';

const API_URL = `${environment.apiUrl}/web/reports`;

export const CUSTOM_REPORTS_URL = `${API_URL}/customreports`;
export const CUSTOM_REPORTS_RESPONSE = 'Fetched Custom Reports';

export const CUSTOM_REPORT_URL = `${API_URL}/customreport`;
export const CUSTOM_REPORT_RESPONSE = 'Fetched Custom Report';

export const CUSTOM_REPORT_TABLES_URL = `${API_URL}/customreporttables`;
export const CUSTOM_REPORT_TABLES_RESPONSE = 'Fetched Custom Report Tables';

export const DELETE_CUSTOM_REPORT_URL = `${API_URL}/deletecustomreport`;
export const DELETE_CUSTOM_REPORT_RESPONSE = 'Deleted Custom Report';

export const SAVE_CUSTOM_REPORT_URL = `${API_URL}/savecustomreport`;
export const SAVE_CUSTOM_REPORT_RESPONSE = 'Saved Custom Report';

export const PRECANNED_REPORTS_URL = `${API_URL}/precannedreports`;
export const PRECANNED_REPORTS_RESPONSE = 'Fetched Pre-Canned Reports';

export const ALL_REPORTS_URL = `${API_URL}/allreports`;
export const ALL_REPORTS_RESPONSE = 'Fetched All Reports';

export const REPORT_HTML_URL = `${API_URL}/reporthtml`;
export const REPORT_HTML_RESPONSE = 'Fetched Report HTML';

export const DOWNLOAD_REPORT_URL = `${API_URL}/downloadreport`;

export const REPORT_SCHEDULES_URL = `${API_URL}/reportschedules`;
export const REPORT_SCHEDULES_RESPONSE = 'Fetched Report Schedules';

export const REPORT_SCHEDULE_URL = `${API_URL}/reportschedule`;
export const REPORT_SCHEDULE_RESPONSE = 'Fetched Report Schedule';

export const SAVE_REPORT_SCHEDULE_URL = `${API_URL}/savereportschedule`;
export const SAVE_REPORT_SCHEDULE_RESPONSE = 'Saved Report Schedule';

export const DELETE_REPORT_SCHEDULE_URL = `${API_URL}/deletereportschedule`;
export const DELETE_REPORT_SCHEDULE_RESPONSE = 'Deleted Report Schedule';

export const REPORT_HISTORY_URL = `${API_URL}/reporthistory`;
export const REPORT_HISTORY_RESPONSE = 'Fetched Report History';

export const DOWNLOAD_SCHEDULED_REPORT_URL = `${API_URL}/downloadscheduledreport`;

@Injectable({
	providedIn: 'root',
})
export class ReportBackendService {
	private backend = inject(BackendService);

	/**
	 * Get Custom Reports
	 * ===================
	 * API Endpoint: /web/reports/customreports
	 *
	 * Returns all Custom Reports
	 */
	getCustomReports(): Observable<CustomReport[]> {
		return this.backend
			.post(CUSTOM_REPORTS_URL, CUSTOM_REPORTS_RESPONSE, {})
			.pipe(
				map((response: CustomReportsResponse) =>
					response.reports.map((json) => CustomReport.fromJSON(json))
				)
			);
	}

	/**
	 * Get Custom Report
	 * ==================
	 * API Endpoint: /web/reports/customreport
	 * JSON Parameters: id
	 *
	 * Returns the requested Custom Report
	 */
	getCustomReport(id: number): Observable<CustomReportResponse> {
		return this.backend
			.post(CUSTOM_REPORT_URL, CUSTOM_REPORT_RESPONSE, { id: id })
			.pipe(map((response: CustomReportResponse) => response));
	}

	/**
	 * Get Custom Report Tables
	 * =========================
	 * API Endpoint: /web/reports/customreporttables
	 *
	 * Returns all Custom Report Tables
	 */
	getCustomReportTables(): Observable<CustomReportTablesResponse> {
		return this.backend
			.post(CUSTOM_REPORT_TABLES_URL, CUSTOM_REPORT_TABLES_RESPONSE, {})
			.pipe(map((response: CustomReportTablesResponse) => response));
	}

	/**
	 * Delete Custom Report
	 * =====================
	 * API Endpoint: /web/reports/deletecustomreport
	 * JSON Parameters: id
	 *
	 * Deletes specified Custom Report
	 */
	deleteCustomReport(id: number): Observable<BaseResponse> {
		return this.backend.post(
			DELETE_CUSTOM_REPORT_URL,
			DELETE_CUSTOM_REPORT_RESPONSE,
			{ id: id }
		);
	}

	/**
	 * Save Custom Report
	 * ===================
	 * API Endpoint: /web/reports/savecustomreport
	 * JSON Parameters: SaveCustomReport
	 *
	 * Saves the supplied Report Schedule
	 */
	saveCustomReport(saveData: SaveCustomReportData): Observable<BaseResponse> {
		return this.backend.post(
			SAVE_CUSTOM_REPORT_URL,
			SAVE_CUSTOM_REPORT_RESPONSE,
			saveData
		);
	}

	/**
	 * Get Pre-Canned Reports
	 * =======================
	 * API Endpoint: /web/reports/precannedreports
	 *
	 * Returns all Pre-Canned Reports
	 */
	getPreCannedReports(): Observable<PreCannedReport[]> {
		return this.backend
			.post(PRECANNED_REPORTS_URL, PRECANNED_REPORTS_RESPONSE, {})
			.pipe(
				map((response: PreCannedReportsResonse) =>
					response.reports.map((json) =>
						PreCannedReport.fromJSON(json)
					)
				)
			);
	}

	/**
	 * Get All Reports
	 * ================
	 * API Endpoint: /web/reports/allreports
	 *
	 * Returns all Pre-Canned & Custom Reports (PreCannedReportJSON format)
	 */
	getAllReports(): Observable<PreCannedReport[]> {
		return this.backend
			.post(ALL_REPORTS_URL, ALL_REPORTS_RESPONSE, {})
			.pipe(
				map((response: PreCannedReportsResonse) =>
					response.reports.map((json) =>
						PreCannedReport.fromJSON(json)
					)
				)
			);
	}

	/**
	 * Report HTML
	 * ============
	 * API Endpoint: /web/reports/reporthtml
	 * JSON Parameters: id
	 *
	 * Returns the MyDBR HTML output for a given Report ID
	 */
	getReportHTML(id: number): Observable<ReportHtmlResonse> {
		return this.backend
			.post(REPORT_HTML_URL, REPORT_HTML_RESPONSE, { id: id })
			.pipe(map((response: ReportHtmlResonse) => response));
	}

	/**
	 * Download Report
	 * ================
	 * API Endpoint: /web/reports/downloadreport
	 * JSON Parameters: id, type
	 *
	 * Returns either binary file or JSON object
	 * Success: binary file that was requested
	 * Error: { "error": Error Message }
	 */
	downloadReport(id: number, type: string): Observable<Blob> {
		return this.backend.download(DOWNLOAD_REPORT_URL, {
			id: id,
			type: type,
		});
	}

	/**
	 * Get Report Schedules
	 * =====================
	 * API Endpoint: /web/reports/reportschedules
	 *
	 * Returns all Report Schedules
	 */
	getReportSchedules(): Observable<ReportSchedule[]> {
		return this.backend
			.post(REPORT_SCHEDULES_URL, REPORT_SCHEDULES_RESPONSE, {})
			.pipe(
				map((response: ReportSchedulesResonse) =>
					response.schedules.map((json) =>
						ReportSchedule.fromJSON(json)
					)
				)
			);
	}

	/**
	 * Get Report Schedule
	 * ====================
	 * API Endpoint: /web/reports/reportschedule
	 * JSON Parameters: id
	 *
	 * Returns the requested Report Schedule
	 */
	getReportSchedule(id: number): Observable<ReportSchedule> {
		return this.backend
			.post(REPORT_SCHEDULE_URL, REPORT_SCHEDULE_RESPONSE, { id: id })
			.pipe(
				map((response: ReportScheduleResonse) =>
					ReportSchedule.fromJSON(response.schedule)
				)
			);
	}

	/**
	 * Save Report Schedule
	 * =====================
	 * API Endpoint: /web/reports/savereportschedule
	 * JSON Parameters: SaveReportSchedule
	 *
	 * Saves the supplied Report Schedule
	 */
	saveReportSchedule(schedule: ReportSchedule): Observable<BaseResponse> {
		return this.backend.post(
			SAVE_REPORT_SCHEDULE_URL,
			SAVE_REPORT_SCHEDULE_RESPONSE,
			schedule.toSaveReportSchedule()
		);
	}

	/**
	 * Delete Report Schedule
	 * =======================
	 * API Endpoint: /web/reports/deletereportschedule
	 * JSON Parameters: id
	 *
	 * Deletes specified Report Schedule
	 */
	deleteReportSchedule(id: number): Observable<BaseResponse> {
		return this.backend.post(
			DELETE_REPORT_SCHEDULE_URL,
			DELETE_REPORT_SCHEDULE_RESPONSE,
			{ id: id }
		);
	}

	/**
	 * Get Report History
	 * ===================
	 * API Endpoint: /web/reports/reporthistory
	 *
	 * Returns all Report History
	 */
	getReportHistory(): Observable<ReportHistory[]> {
		return this.backend
			.post(REPORT_HISTORY_URL, REPORT_HISTORY_RESPONSE, {})
			.pipe(
				map((response: ReportHistoryResonse) =>
					response.history.map((json) => ReportHistory.fromJSON(json))
				)
			);
	}

	/**
	 * Download Scheduled Report
	 * ==========================
	 * API Endpoint: /web/reports/downloadscheduledreport
	 * JSON Parameters: filename
	 *
	 * Returns either binary file or JSON object
	 * Success: binary file that was requested
	 * Error: { "error": Error Message }
	 */
	downloadScheduledReports(report: ReportHistory): Observable<ReportHistory> {
		return this.backend
			.download(DOWNLOAD_SCHEDULED_REPORT_URL, {
				filename: report.filename,
			})
			.pipe(map((blob) => ReportHistory.addFile(blob, report)));
	}
}
